/**
 * Display Context - Front desk display state
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { accountApi, apiErrorText } from '@/lib/pwaAccount';

const DisplayContext = createContext(null);

export function DisplayProvider({ children }) {
  const [orders, setOrders] = useState({
    preparing: [],
    ready: []
  });
  const [stats, setStats] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const lastReadyCount = useRef(0);

  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdHmBfnR6fn5/gIGBg4KCgYGBgYKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKBgYGBgYKCgoKCgoKCgoKCgoKCgoKC');
  }, []);

  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  const fetchOrders = useCallback(async () => {
    try {
      // the signed-in restaurant's orders only (its account token, lib/pwaAccount)
      const response = await accountApi.get('/api/display/orders');
      const data = response.data || { preparing: [], ready: [] };
      
      // Play sound for new ready orders
      if (data.ready.length > lastReadyCount.current && lastReadyCount.current > 0) {
        playSound();
      }
      lastReadyCount.current = data.ready.length;
      
      setOrders(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch display orders:', err);
      // Keep the last orders the API returned; never show made-up ones
      setError(apiErrorText(err, 'Could not load orders'));
    } finally {
      setLoading(false);
    }
  }, [playSound]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await accountApi.get('/api/display/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch display stats:', error);
    }
  }, []);

  const fetchPromotions = useCallback(async () => {
    try {
      const response = await accountApi.get('/api/display/promotions');
      setPromotions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch display promotions:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchPromotions();
    
    const orderInterval = setInterval(fetchOrders, 5000);
    const statsInterval = setInterval(fetchStats, 30000);
    const promoInterval = setInterval(fetchPromotions, 60000);
    
    return () => {
      clearInterval(orderInterval);
      clearInterval(statsInterval);
      clearInterval(promoInterval);
    };
  }, [fetchOrders, fetchStats, fetchPromotions]);

  return (
    <DisplayContext.Provider value={{
      orders,
      stats,
      promotions,
      soundEnabled,
      loading,
      error,
      setSoundEnabled,
      refresh: () => { fetchOrders(); fetchStats(); }
    }}>
      {children}
    </DisplayContext.Provider>
  );
}

export const useDisplay = () => {
  const context = useContext(DisplayContext);
  if (!context) {
    throw new Error('useDisplay must be used within a DisplayProvider');
  }
  return context;
};
